import { faFilter, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import React, { Fragment, useContext, useEffect, useState } from "react";
import { Button, Spinner, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import SwaggerContext from "../../SwaggerContext";
import { notifyError, notifySuccess } from "../../actions/notifications";
import ConfirmationModal from "../../components/ConfirmationModal";
import FilterModal from "../../components/FilterModal";
import PaginationComponent from "../../components/pagination";
import { propTypes } from "react-bootstrap/esm/Image";
import { createApiCallback } from "../../utils/error-handler";

const initialState = {
  pagination: {
    page: 0,
    limit: 10,
  },
};

const loadPages = (pageApi, setData, opts) => {
  pageApi.getPages(opts, createApiCallback(
    (data) => {
      setData(data);
    },
    (errorMessage) => {
      notifyError(`Ha habido un error al cargar la lista de páginas: ${errorMessage}`);
    }
  ));
};

const removePage = (pageApi, setData, id, pageNumber) => {
  pageApi.deletePageById(id, createApiCallback(
    () => {
      notifySuccess("Página eliminada correctamente.");
      let opts = initialState.pagination;
      opts.page = pageNumber;
      loadPages(pageApi, setData, opts);
    },
    (errorMessage) => {
      notifyError(`Ha habido un error al borrar la página: ${errorMessage}`);
    },
    204
  ));
};

const PagesTable = React.memo(
  ({ pageApi, pages, setData, pageNumber, filter, setFilter }) => {
    const pageHeaders = [
      { type: "type", name: "Tipo" },
      { type: "categoryId", name: "Categoría" },
      { type: "title", name: "Título" },
      { type: "enabled", name: "Activa" },
    ];
    const [deleteArguments, setDeleteArguments] = useState();
    const [showModal, setShowModal] = useState(false);
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [filterType, setFilterType] = useState(""); // Estado para `filterType`
    const [loadCategories, setLoadCategories] = useState(false); // Estado para `filterType`

    const navigate = useNavigate();

    return (
      <Table striped bordered hover variant="dark mt-1 text-center">
        <FilterModal
          showModal={showFilterModal}
          setShowModal={setShowFilterModal}
          filterType={filterType}
          filter={filter}
          setFilter={setFilter} // Actualiza el filtro desde el modal
          loadCategories={loadCategories}
        />
        <thead>
          <tr>
            <th>#</th>
            {pageHeaders.map((header, i) => {
              return (
                <th key={i}>
                  {header.name}{" "}
                  <FontAwesomeIcon
                    style={{ cursor: "pointer" }}
                    icon={faFilter}
                    onClick={() => {
                      setFilterType(header.type); // Configura el tipo de filtro
                      setShowFilterModal(true); // Abre el modal de filtro
                      setLoadCategories(header.name === "Categoría"); // Activa categorías si es "Categoría"
                    }}
                  />
                </th>
              );
            })}
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {pages.map((page, i) => {
            return (
              <tr
                key={i}
                onClick={() => navigate(`/paginas/editar/${page.id}`)}
                style={{ cursor: "pointer" }}
              >
                <td>{i + 1}</td>
                <td>{page.type}</td>
                <td>{page.category_name}</td>
                <td>{page.title}</td>
                <td>
                  {page.enabled ? "✅" : "❌"}
                </td>                
                <td>
                  <Button
                    variant="danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowModal(true);
                      setDeleteArguments([
                        pageApi,
                        setData,
                        page.id,
                        pageNumber,
                      ]);
                    }}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
        <ConfirmationModal
          show={showModal}
          setShowModal={setShowModal}
          deleteFunction={removePage}
          deleteArguments={deleteArguments}
        />
      </Table>
    );
  }
);

PagesTable.displayName = "PagesTable";
PagesTable.propTypes = {
  pages: PropTypes.array,
  setData: PropTypes.func,
  pageApi: PropTypes.object,
  pageNumber: PropTypes.number,
  filter: propTypes.object,
  setFilter: propTypes.func,
};

const List = () => {
  const { pageApi } = useContext(SwaggerContext);
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const [filter, setFilter] = useState({
    type: null,
    title: null,
    categoryId: null,
    enabled: null,
  });
  let [opts, setOpts] = useState(initialState.pagination);
  
  // Cargar páginas cuando cambien opts o filter
  useEffect(() => {
    loadPages(pageApi, setData, { ...opts, ...filter });
  }, [opts, filter, pageApi]);

  if (null === data) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "calc(100vh - 200px)" }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <Fragment>
      <Button variant="success" onClick={() => navigate("/paginas/nueva")}>
        <FontAwesomeIcon icon={faPlus} />
      </Button>
      <PagesTable
        pages={data.pages}
        setData={setData}
        pageApi={pageApi}
        pageNumber={data.page}
        filter={filter}
        setFilter={setFilter}
      />
      <div className="d-flex justify-content-center align-items-center">
        <PaginationComponent pages={data.totalPages} setOpts={setOpts} currentPage={data.page} />
      </div>
    </Fragment>
  );
};

export default List;
